<template>
  <div class="patient-journeys--wrap">
    <v-container>
      <v-btn
        v-if="isAdvisor"
        @click="openCreateJourneyDialog"
        class="patient-journeys--btn"
        elevation="0"
        rounded
        large
        ><span>Create New Journey</span></v-btn
      >

      <!-- Loading state -->
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <!-- Journeys list -->
      <div v-if="!loading && hasJourneys">
        <journey-card
          v-for="journey in journeys"
          :key="journey.id"
          :journey="journey"
          @print-journey="printJourney"
        />
      </div>

      <!-- Empty state -->
      <div v-else-if="!loading && !hasJourneys" class="journeys-empty-state">
        <v-container>
          <v-row class="fill-height">
            <v-col class="text-center">
              <v-img
                class="journeys-empty-image mx-auto mb-4"
                alt="Empty journeys image"
                src="@/assets/emptyChat.svg"
                contain
                height="310"
                width="390"
              />
              <h3 class="journeys-empty-title">
                No journeys found
              </h3>
              <p class="journeys-empty-subtitle">
                Create your first journey to get started
              </p>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-container>
    <create-journey-dialog
      :showDialog="createJourneyDialog"
      :profileId="profileId"
      @on-submit="createJourney"
      @close-dialog="createJourneyDialog = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import JourneyCard from '@/components/journeys/JourneyCard.vue'
import CreateJourneyDialog from '@/components/dialogs/CreateJourneyDialog.vue'

export default {
  components: {
    JourneyCard,
    CreateJourneyDialog,
  },
  name: 'PatientJourneys',
  data: () => ({
    createJourneyDialog: false,
  }),
  computed: {
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('journeysStates', ['loading']),
    ...mapState('patientsStates', ['currentPatient']),
    ...mapGetters('journeysStates', ['journeys', 'hasJourneys']),
    profileId() {
      return this.currentPatient?.profileId?.toString()
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
  },
  async mounted() {
    if (this.profileId) {
      await this.getJourneys({ profileId: this.profileId })
    }
  },
  watch: {
    currentPatient(patient) {
      if (patient?.profileId) {
        this.getJourneys({ profileId: patient.profileId })
      }
    },
    $route(to, from) {
      // get new current patient data when patient id changed
      if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
        this.getPatient(to.params.id)
      }
    },
  },
  methods: {
    ...mapActions('journeysStates', ['getJourneys', 'generateJourneyPdf']),
    ...mapActions('patientsStates', ['getPatient']),
    openCreateJourneyDialog() {
      this.createJourneyDialog = true
    },
    async createJourney() {
      this.createJourneyDialog = false
      // Refresh journeys after creating a new one
      if (this.profileId) {
        await this.getJourneys({ profileId: this.profileId })
      }
    },
    async printJourney(journeyId) {
      if (!this.profileId) {
        return
      }
      await this.generateJourneyPdf({ profileId: this.profileId, journeyId })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.patient-journeys {
  &--wrap {
    padding: 2 * $gutter;
  }
  &--btn {
    margin-bottom: 2 * $gutter;
    border: 1px solid $bh-gray--5;
    background: $bh-blue--5 !important;
    width: 100%;
    height: 72px !important;
    span {
      font-size: 16px;
      line-height: 1.25;
      font-weight: 500;
      text-align: center;
      color: $bh-black;
    }
  }
}

.journeys-empty-state {
  margin-top: 2 * $gutter;

  .journeys-empty-image {
    opacity: 0.6;
  }

  .journeys-empty-title {
    font-size: 24px;
    font-weight: 500;
    color: $bh-black;
    margin-bottom: 8px;
  }

  .journeys-empty-subtitle {
    font-size: 16px;
    color: $bh-gray--3;
    margin-bottom: 0;
  }
}
</style>
